import React, { useEffect, useState } from "react";
import { DataCorrectionTable } from "./DataCorrectionPage";
import {
  getDataCorrectionRequests,
  approveCorrectionRequest,
  rejectCorrectionRequest,
  downloadCorrectionFile,
} from "../api";
import StatusBadge from "./StatusBadge";
import DataCorrectionUploader from "./DataCorrectionUploader";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { PERMISSIONS } from "../utils/permissions";

const TAB_ALL = "ALL";
const TAB_PENDING = "PENDING";
const TAB_APPROVED = "APPROVED";
const TAB_REJECTED = "REJECTED";
const TAB_MY_UPLOADS = "MY_UPLOADS";
const TAB_REVERTED = "REVERTED";

function buildIssueRows(processingResult) {
  if (!processingResult || !Array.isArray(processingResult.batchResults)) {
    return [];
  }

  const rows = [];

  processingResult.batchResults.forEach((batch) => {
    (batch.skippedRecords || []).forEach((rec) => {
      const mergedErrors = [];

      (rec.columnErrors || []).forEach((e) => {
        mergedErrors.push({
          column: e.column,
          message: e.error,
        });
      });

      (rec.skippedColumns || []).forEach((col) => {
        mergedErrors.push({
          column: col.column,
          message: col.reason || "Not mapped in backend",
        });
      });

      const uniqueMessages =
        mergedErrors.length > 0
          ? Array.from(
            new Map(
              mergedErrors.map((e) => [`${e.column}:${e.message}`, e])
            ).values()
          ).map((e) => `${e.column}: ${e.message}`)
          : ["No applicable fields to update"];

      rows.push({
        type: rec.isUnmappedOnly ? "Unmapped columns" : "Skipped",
        rowNumber: rec.rowNumber,
        linkedinUrl:
          rec.row?.linkedin_url ||
          rec.row?.normalized_linkedin_url ||
          rec.row?.linkedin ||
          rec.row?.company_linkedin_url ||
          "",
        reason:
          rec.reason ||
          (rec.isUnmappedOnly
            ? "No applicable fields to update"
            : "Row skipped"),
        details: uniqueMessages,
      });
    });

    (batch.updateErrors || []).forEach((err) => {
      rows.push({
        type: "Update Error",
        rowNumber: err.row,
        linkedinUrl: err.linkedinUrl || "",
        reason: err.error || "Update failed",
        details: [],
      });
    });
  });

  return rows;
}

export default function DataCorrectionApprover({ onViewLogs }) {
  const { hasPermission } = useAuth();
  const { theme } = useTheme();

  const canApprove = hasPermission(PERMISSIONS.DATA_CORRECTION_APPROVER);
  const canUpload = hasPermission(PERMISSIONS.DATA_CORRECTION_UPLOADER);

  const tabs = [];
  if (canApprove) {
    tabs.push(
      { id: TAB_ALL, label: "ALL" },
      { id: TAB_PENDING, label: "PENDING" },
      { id: TAB_APPROVED, label: "APPROVED" },
      { id: TAB_REJECTED, label: "REJECTED" }
    );
  }
  if (canUpload) {
    tabs.push({ id: TAB_MY_UPLOADS, label: "MY UPLOADS/ NEW UPLOADS" });
  }

  if (canApprove || canUpload) {
    tabs.push({ id: TAB_REVERTED, label: "REVERTED FILES" });
  }

  const defaultTab = canApprove ? TAB_PENDING : TAB_MY_UPLOADS;

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingApproval, setProcessingApproval] = useState(false);
  const [processingMeta, setProcessingMeta] = useState({ requestId: null, fileName: null });

  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);

  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summary, setSummary] = useState(null);

  const renderStatusCell = (r) => {
    if (r.reverted) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <StatusBadge status={r.status} />
          <span
            style={{
              fontSize: 10,
              padding: "2px 6px",
              borderRadius: 999,
              border: "1px solid #f97316",
              background: "#fff7ed",
              color: "#c05621",
              textTransform: "uppercase",
            }}
          >
            Reverted
          </span>
        </div>
      );
    }
    return <StatusBadge status={r.status} />;
  };

  const sortByRevertedDesc = (arr) =>
    [...arr].sort(
      (a, b) =>
        new Date(b.reverted_at || b.uploaded_at || 0) -
        new Date(a.reverted_at || a.uploaded_at || 0)
    );

  const loadRequestsForTab = async (tabId) => {
    if (tabId === TAB_MY_UPLOADS) {
      setRequests([]);
      return;
    }

    try {
      setLoading(true);
      setMessage(null);
      setMessageType(null);

      if (tabId === TAB_REVERTED) {
        if (canApprove) {
          const res = await getDataCorrectionRequests("APPROVED");
          const reverted = sortByRevertedDesc(
            (res.data || []).filter((r) => r.reverted)
          );
          setRequests(reverted);
        } else if (canUpload) {
          const res = await getDataCorrectionRequests();
          const reverted = sortByRevertedDesc(
            (res.data || []).filter((r) => r.reverted)
          );
          setRequests(reverted);
        } else {
          setRequests([]);
        }
        return;
      }

      if (!canApprove) {
        setRequests([]);
        return;
      }

      const effectiveStatus = tabId === TAB_ALL ? null : tabId;
      const res = await getDataCorrectionRequests(effectiveStatus);
      setRequests(res.data || []);
    } catch (err) {
      console.error("Failed to load requests", err);
      setMessage("Failed to load requests.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === TAB_MY_UPLOADS) return;
    if (!canApprove && !canUpload) return;
    loadRequestsForTab(activeTab);
  }, [activeTab, canApprove, canUpload]);

  const handleDownload = async (id) => {
    try {
      const res = await downloadCorrectionFile(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${id}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed", err);
      setMessage("Failed to download CSV.");
      setMessageType("error");
    }
  };

  const handleApprove = async (request) => {
    setMessage(null);
    setMessageType(null);
    try {
      setProcessingMeta({ requestId: request.request_id, fileName: request.file_name });
      setProcessingApproval(true);
      const res = await approveCorrectionRequest(request.request_id);

      const processingResult = res.data?.processingResult;
      setSummary({
        requestId: res.data?.request_id,
        fileName: request.file_name,
        processingResult,
      });
      setSummaryOpen(true);

      setMessage("Request approved and processed.");
      setMessageType("success");

      await loadRequestsForTab(activeTab);
    } catch (err) {
      console.error("Approve failed", err);
      const backendMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to approve request.";
      setMessage(backendMsg);
      setMessageType("error");
    } finally {
      setProcessingApproval(false);
    }
  };

  const handleReject = async (request) => {
    setMessage(null);
    setMessageType(null);
    try {
      await rejectCorrectionRequest(request.request_id);
      setMessage("Request rejected.");
      setMessageType("success");
      await loadRequestsForTab(activeTab);
    } catch (err) {
      console.error("Reject failed", err);
      const backendMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to reject request.";
      setMessage(backendMsg);
      setMessageType("error");
    }
  };

  const issueRows = summary ? buildIssueRows(summary.processingResult) : [];

  return (
    <div style={{ padding: "30px", maxWidth: "1400px", width: "100%" }}>
      <h2 style={{ marginBottom: "10px" }}>Data Correction</h2>

      <style>{`
        @keyframes dataCorrectionSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {processingApproval && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              width: "90%",
              maxWidth: "720px",
              background: theme.cardBackground,
              borderRadius: "12px",
              padding: "22px 24px",
              border: `1px solid ${theme.border}`,
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
            }}
          >
            <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  border: "4px solid #e5e7eb",
                  borderTopColor: "#3b82f6",
                  animation: "dataCorrectionSpin 0.8s linear infinite",
                }}
              />
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, color: theme.text, fontSize: 16 }}>Processing CSV</h3>
                <p
                  style={{
                    margin: "8px 0 0",
                    color: theme.textSecondary,
                    fontSize: 13,
                    lineHeight: 1.4,
                  }}
                >
                  Your approval is being processed. Please wait—this may take a few moments depending on the number of rows.
                </p>
                {processingMeta?.requestId && (
                  <p
                    style={{
                      margin: "10px 0 0",
                      color: theme.textSecondary,
                      fontSize: 12,
                      wordBreak: "break-word",
                    }}
                  >
                    Request ID: {processingMeta.requestId}
                    {processingMeta.fileName ? ` • ${processingMeta.fileName}` : ""}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {message && (
        <div
          style={{
            margin: "10px 0 20px",
            padding: "10px 14px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 500,
            background:
              messageType === "success"
                ? "#e8f8f0"
                : messageType === "error"
                  ? "#fdecea"
                  : "#e5f2ff",
            border:
              messageType === "success"
                ? "1px solid #c8e6d9"
                : messageType === "error"
                  ? "1px solid #f5c2c7"
                  : "1px solid #b3d4ff",
            color:
              messageType === "success"
                ? "#148f50"
                : messageType === "error"
                  ? "#b71c1c"
                  : "#1d4ed8",
          }}
        >
          {message}
        </div>
      )}

      {/* Tabs row */}
      <div style={{ display: "flex", gap: "10px", margin: "15px 0 20px" }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "8px 14px",
              fontSize: "13px",
              fontWeight: tab.id === activeTab ? 600 : 400,
              background: tab.id === activeTab ? "#3498db" : "#ecf0f1",
              color: tab.id === activeTab ? "#fff" : "#2c3e50",
              border: "none",
              borderRadius: "999px",
              cursor: "pointer",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* If active tab is My Uploads → show uploader UI and stop here */}
      {activeTab === TAB_MY_UPLOADS && canUpload && (
        <DataCorrectionUploader onViewLogs={onViewLogs} />
      )}

      {/* Approver/uploader view for list of requests (includes REVERTED FILES tab) */}
      {activeTab !== TAB_MY_UPLOADS && (canApprove || activeTab === TAB_REVERTED) && (
        <>
          {/* Requests Table */}
          <div
            style={{
              background: "#fff",
              borderRadius: "10px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
              overflowX: "auto",
            }}
          >
            <DataCorrectionTable
              requests={requests}
              loading={loading}
              isRevertedTab={activeTab === TAB_REVERTED}
              canApprove={canApprove}
              onDownload={handleDownload}
              onApprove={handleApprove}
              onReject={handleReject}
              onViewLogs={onViewLogs}
            />
          </div>

          {/* Summary Modal (unchanged from your version) */}
          {summaryOpen && summary && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
              }}
            >
              <div
                style={{
                  width: "80%",
                  maxWidth: "900px",
                  maxHeight: "80vh",
                  background: theme.cardBackground,
                  borderRadius: "12px",
                  padding: "20px 24px",
                  overflow: "auto",
                  border: `1px solid ${theme.border}`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <h3 style={{ margin: 0, color: theme.text }}>Processing Summary</h3>
                  <button
                    onClick={() => setSummaryOpen(false)}
                    style={{
                      border: "none",
                      background: "transparent",
                      fontSize: "18px",
                      cursor: "pointer",
                      color: theme.textMuted,
                    }}
                  >
                    ✖
                  </button>
                </div>

                <div
                  style={{
                    fontSize: "13px",
                    color: theme.textSecondary,
                    marginBottom: 12,
                  }}
                >
                  <div>
                    <strong>Request ID:</strong> {summary.requestId}
                  </div>
                  <div>
                    <strong>File:</strong> {summary.fileName}
                  </div>
                </div>

                {summary.processingResult && (
                  <>
                    <div
                      style={{
                        display: "flex",
                        gap: "10px",
                        flexWrap: "wrap",
                        marginBottom: "15px",
                      }}
                    >
                      <StatChip
                        label="Total rows"
                        value={summary.processingResult.total}
                      />
                      <StatChip
                        label="Processed"
                        value={summary.processingResult.processed}
                      />
                      <StatChip
                        label="Updated"
                        value={summary.processingResult.totalUpdated}
                      />
                      <StatChip
                        label="Skipped"
                        value={summary.processingResult.totalSkipped}
                      />
                      <StatChip label="Issues" value={issueRows.length} />
                    </div>

                    {summary.processingResult.successfulRows?.length > 0 && (
                      <>
                        <h4 style={{ color: "#148f50" }}>
                          Successfully Updated (
                          {summary.processingResult.successfulRows.length})
                        </h4>
                        <table width="100%" cellPadding={8}>
                          <thead style={{ background: "#e8f8f0" }}>
                            <tr>
                              <th align="left">Row</th>
                              <th align="left">LinkedIn URL</th>
                            </tr>
                          </thead>
                          <tbody>
                            {summary.processingResult.successfulRows.map(
                              (r, i) => (
                                <tr key={i}>
                                  <td>{r.rowNumber}</td>
                                  <td style={{ wordBreak: "break-all" }}>
                                    {r.linkedin_url}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </>
                    )}

                    {issueRows.length > 0 && (
                      <>
                        <h4>Issues ({issueRows.length})</h4>
                        <table width="100%" cellPadding={8}>
                          <thead style={{ background: "#f9fafb" }}>
                            <tr>
                              <th align="left">Row</th>
                              <th align="left">Type</th>
                              <th align="left">LinkedIn URL</th>
                              <th align="left">Reason</th>
                              <th align="left">Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {issueRows.map((row, idx) => (
                              <tr key={idx}>
                                <td>{row.rowNumber}</td>
                                <td>{row.type}</td>
                                <td style={{ wordBreak: "break-all" }}>
                                  {row.linkedinUrl}
                                </td>
                                <td>{row.reason}</td>
                                <td>
                                  {row.details && row.details.length > 0 ? (
                                    <ul
                                      style={{
                                        margin: 0,
                                        paddingLeft: 16,
                                      }}
                                    >
                                      {row.details.map((d, i2) => (
                                        <li key={i2}>{d}</li>
                                      ))}
                                    </ul>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}

                    {issueRows.length === 0 &&
                      (!summary.processingResult.successfulRows ||
                        summary.processingResult.successfulRows.length === 0) && (
                        <div
                          style={{
                            marginTop: "10px",
                            padding: "10px 12px",
                            borderRadius: "8px",
                            background: "#e8f8f0",
                            border: "1px solid #c8e6d9",
                            color: "#148f50",
                            fontSize: "14px",
                          }}
                        >
                          ✅ All rows processed without issues.
                        </div>
                      )}
                  </>
                )}

                {summary.processingResult && summary.processingResult.enrichment && (
                  <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                    <h4 style={{ marginBottom: '10px' }}>Enrichment Results</h4>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                      <StatChip label="Enriched" value={summary.processingResult.enrichment.enriched} />
                      <StatChip label="Skipped" value={summary.processingResult.enrichment.skipped} />
                    </div>

                    {summary.processingResult.enrichment.successfulRows && summary.processingResult.enrichment.successfulRows.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <h5 style={{ color: '#148f50', margin: '0 0 10px' }}>Successfully Updated ({summary.processingResult.enrichment.successfulRows.length})</h5>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '6px' }}>
                          <table width="100%" cellPadding={8} style={{ borderCollapse: 'collapse' }}>
                            <thead style={{ background: '#e8f8f0', position: 'sticky', top: 0 }}>
                              <tr>
                                <th align="left" style={{ fontSize: '12px' }}>Row</th>
                                <th align="left" style={{ fontSize: '12px' }}>LinkedIn URL</th>
                              </tr>
                            </thead>
                            <tbody>
                              {summary.processingResult.enrichment.successfulRows.map((r, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                  <td style={{ fontSize: '13px' }}>{r.rowNumber}</td>
                                  <td style={{ fontSize: '13px', wordBreak: 'break-all' }}>{r.linkedin_url}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {summary.processingResult.enrichment.skippedRows && summary.processingResult.enrichment.skippedRows.length > 0 && (
                      <div>
                        <h5 style={{ color: '#b91c1c', margin: '0 0 10px' }}>Enrichment Issues ({summary.processingResult.enrichment.skippedRows.length})</h5>
                        <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '6px' }}>
                          <table width="100%" cellPadding={8} style={{ borderCollapse: 'collapse' }}>
                            <thead style={{ background: '#fef2f2', position: 'sticky', top: 0 }}>
                              <tr>
                                <th align="left" style={{ fontSize: '12px' }}>Row</th>
                                <th align="left" style={{ fontSize: '12px' }}>LinkedIn URL</th>
                                <th align="left" style={{ fontSize: '12px' }}>Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {summary.processingResult.enrichment.skippedRows.map((r, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                  <td style={{ fontSize: '13px' }}>{r.rowNumber}</td>
                                  <td style={{ fontSize: '13px', wordBreak: 'break-all' }}>{r.linkedin_url || '-'}</td>
                                  <td style={{ fontSize: '13px' }}>{r.reason}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatChip({ label, value }) {
  return (
    <div
      style={{
        padding: "6px 10px",
        borderRadius: "999px",
        background: "#f3f4f6",
        fontSize: "12px",
      }}
    >
      <strong style={{ marginRight: "4px" }}>{value ?? 0}</strong>
      <span style={{ color: "#666" }}>{label}</span>
    </div>
  );
}
